import React, { lazy, useContext, useEffect, useMemo, useState } from 'react'
import { TableBody, TableCell, TableRow, IconButton } from '@material-ui/core'
import FeatherIcon from 'feather-icons-react'
import objectPath from 'object-path'
import Icon from '../../Icon'
import './style.css'
import RenderRow from './RenderRow'
import { genRefId, getRowId } from '../action/index'
import { useContextMenu } from 'react-contexify'
import { onRowTableUpdate } from '../action/EditRow'
import { DesignContext } from 'contexts/DesignContext'

const TableMenu = lazy(() =>
  import('designer/component/element/table/TableMenu'),
)
const PrintMenu = lazy(() =>
  import('designer/component/element/table/PrintMenu'),
)

const SIZE_ICON = 20

const getActionStyle = (uischema) => {
  let { action = [], optional = [] } = uischema.tableAction
  let activeAction = action.filter(
    ({ isActive, name }) => isActive && name !== 'add',
  )
  let activeIcon = optional.filter(
    ({ actionType }) => actionType === 'showIcon',
  )
  let activeOptional = optional.filter(
    ({ actionType }) => actionType !== 'showIcon',
  )
  let actionCount = activeAction.length
  let iconCount = activeIcon.length
  let optionalCount = activeOptional.length > 0 ? 1 : 0
  let countIcon = actionCount + iconCount + optionalCount
  let width = countIcon * 50
  return {
    padding: uischema.rowPadding,
    minWidth: width + 'px',
    maxWidth: width + 'px',
  }
}

const getStableRowKey = (rowData, uischema, index) => {
  const ref = objectPath.get(rowData, genRefId)
  const id = ref ?? getRowId(rowData, uischema)
  const base = id != null && id !== '' ? String(id) : 'row'
  return `${base}-${index}`
}

const ActionColumn = (props) => {
  const {
    uischema,
    rowData,
    rowKey,
    isActiveDelete,
    activeIcon,
    activeOptional,
    onDeleteRow,
  } = props
  const styleAction = getActionStyle(uischema)
  const { stateParent = {} } = props
  const {
    dataRowSelected: [dataRowSelected, setDataRowSelected],
  } = stateParent
  
  const designContext = useContext(DesignContext)
  const { jsonForm, isUiChanged, setIsUiChanged } = designContext
  const columnRefId = objectPath.get(uischema, 'loadData.columnRefId')

  let formData = jsonForm.formData
  let jsonTable =
    objectPath.get(formData, uischema.name) ||
    objectPath.get(formData, uischema.key)

  const propAction = {
    ...props,
    jsonTable,
    designContext,
    formData,
  }

  const setModalAction = (modalAction) => {
    objectPath.get(isUiChanged, ['modalAction', uischema.key])
    setIsUiChanged(isUiChanged)
  }

  const { show } = useContextMenu({
    id: 'menuId',
    props: {
      key: 'value',
    },
  })
  const menuId = `table-menu-${uischema.key}-${rowKey}`

  const displayTableMenu = (e) =>
    show(e, {
      id: menuId,
    })

  const displayPrintMenu = (e) =>
    show(e, {
      id: 'print-menu',
    })

  return (
    <TableCell style={styleAction} align="center">
      <>
        {activeIcon.map((item) => {
          return (
            <IconButton className="p-2" onClick={() => {}}>
              <Icon iconName={item.icon} size={SIZE_ICON} />
            </IconButton>
          )
        })}
        {activeOptional.length > 0 && (
          <IconButton
            className="p-2"
            onClick={async (event) => {
              setDataRowSelected(rowData)
              displayTableMenu(event)
            }}
          >
            <FeatherIcon
              size={SIZE_ICON}
              icon="more-vertical"
              className="text-dark"
            />
            <TableMenu
              menuId={menuId}
              option={activeOptional}
              dataSelected={rowData}
              uischema={uischema}
              renderTableData={() => {}}
              setModalAction={setModalAction}
              onRowTableUpdate={onRowTableUpdate}
              genRefId={genRefId}
              columnRefId={columnRefId}
              propAction={propAction}
              style={{ boxShadow: 'rgba(0, 0, 0, 0.03) 0 9px 20px 2px' }}
            />
          </IconButton>
        )}
        {isActiveDelete && (
          <IconButton className="p-2" onClick={() => onDeleteRow(rowData)}>
            <FeatherIcon
              size={SIZE_ICON}
              icon="trash"
              className="text-danger"
            />
          </IconButton>
        )}
      </>
    </TableCell>
  )
}

const MTableBody = (props) => {
  const {
    tableData,
    page,
    rowsPerPage,
    uischema,
    editData,
    setEditData,
    showAction,
    isReadOnly,
    openModalTable,
  } = props
  const { isPaging } = uischema || {}
  const isSelectRowOnTab =
    objectPath.get(uischema, 'action.name') === 'selectRow' &&
    objectPath.get(uischema, 'action.selectType') === 'onTab'
  const isRowClick =
    objectPath.get(uischema, 'action.event') === 'onRowClick' ||
    isSelectRowOnTab
  const { rowColor1, rowColor2 } = uischema
  let rows = tableData || []
  let seq = page === 0 ? 1 : page * rowsPerPage + 1

  const visibleRows = useMemo(() => {
    return rows.filter((rowData) => !objectPath.get(rowData, '@isDeleted'))
  }, [rows, openModalTable])

  const paginatedRows = useMemo(() => {
    return isPaging
      ? visibleRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : visibleRows
  }, [visibleRows, isPaging, page, rowsPerPage])

  return (
    <TableBody>
      {paginatedRows.map((rowData, index) => {
        let rowIndex = isPaging ? page * rowsPerPage + index : index
        let rowColor = index % 2 === 0 ? rowColor1 : rowColor2
        rowData = { ...rowData, '@seq': seq }
        seq++
        let key = getStableRowKey(rowData, uischema, rowIndex)

        return (
          <TableRow
            key={key}
            className="custom-table-row"
            style={{
              cursor: isRowClick ? 'pointer' : 'default',
              backgroundColor: rowColor,
            }}
          >
            {showAction && (
              <ActionColumn
                {...props}
                rowData={rowData}
                rowKey={key}
                key={`${key}-action`}
              />
            )}
            <RenderRow
              {...props}
              rowData={rowData}
              editData={editData}
              setEditData={setEditData}
              key={`${key}-cells`}
            />
          </TableRow>
        )
      })}
    </TableBody>
  )
}

export default MTableBody
